/**
 * Cliente para chamadas às Edge Functions do Supabase
 * Substitui as chamadas às API routes do Next.js
 */

import { createClient } from '@/lib/supabase/client'

const supabase = createClient()

/**
 * Obtém a URL base das Edge Functions
 */
function getEdgeFunctionUrl(functionName: string): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL não configurada no .env')
  }

  // Remove trailing slash se existir
  const baseUrl = supabaseUrl.replace(/\/$/, '')
  return `${baseUrl}/functions/v1/${functionName}`
}

/**
 * Faz uma chamada para uma Edge Function
 */
export async function callEdgeFunction<T = any>(
  functionName: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
    body?: any
    headers?: Record<string, string>
  } = {}
): Promise<T> {
  const { method = 'POST', body, headers = {} } = options

  const url = getEdgeFunctionUrl(functionName)
  
  // Obter sessão e garantir que está atualizada
  // getSession() verifica localStorage e renova automaticamente se necessário
  const { data: { session }, error: sessionError } = await supabase.auth.getSession()
  
  if (sessionError) {
    console.error('Error getting session:', sessionError)
    throw new Error('Erro ao obter sessão de autenticação. Por favor, faça login novamente.')
  }

  const token = session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY || ''

  if (!token) {
    throw new Error('Token de autenticação não encontrado. Por favor, faça login novamente.')
  }

  const response = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    // Tenta ler o erro
    let errorData = { error: 'Erro desconhecido' }
    try {
      errorData = await response.json()
    } catch {
      // Ignora erro de parse se o body estiver vazio
    }
    
    // Se for erro 401 (Unauthorized), tentar refresh do token e retry
    if (response.status === 401) {
      console.warn('Unauthorized request - attempting token refresh and retry...')
      
      // Tentar refresh forçado
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession()
      
      if (!refreshError && refreshData.session) {
        console.log('Token refreshed successfully, retrying request...')
        const newToken = refreshData.session.access_token
        
        // Tentar novamente com o novo token
        const retryResponse = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${newToken}`,
            ...headers,
          },
          body: body ? JSON.stringify(body) : undefined,
        })
        
        if (retryResponse.ok) {
          return retryResponse.json()
        } else {
           // Se falhar novamente, lê o erro da segunda tentativa
           let retryErrorData = { error: 'Erro desconhecido no retry' }
           try {
             retryErrorData = await retryResponse.json()
           } catch {}
           throw new Error(retryErrorData.error || `Erro na chamada após retry: ${retryResponse.statusText}`)
        }
      } else {
         console.error('Failed to refresh token:', refreshError)
         throw new Error('Sessão expirada. Por favor, faça login novamente.')
      }
    }
    
    throw new Error(errorData.error || `Erro na chamada: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Helpers específicos para cada função
 */

// Notificações
export const notificationsApi = {
  get: async (userId: string, page = 1, pageSize = 10) => {
    return callEdgeFunction('notifications', {
      method: 'GET',
      // Query params serão adicionados pela Edge Function
    })
  },
  create: async (data: any) => {
    return callEdgeFunction('notifications', {
      method: 'POST',
      body: data,
    })
  },
}

// Admin
export const adminApi = {
  inviteUser: async (data: { email: string; name: string; phone?: string; address?: string }) => {
    return callEdgeFunction('admin-invite-user', {
      method: 'POST',
      body: data,
    })
  },
}

// Pipefy
export const pipefyApi = {
  syncDatabases: async (data: { centroCustoDatabaseId: string; tipoDespesaDatabaseId: string }) => {
    return callEdgeFunction('sync-databases-pipefy', {
      method: 'POST',
      body: data,
    })
  },
  syncOrcamento: async (data: {
    orcamentoDatabaseId: string
    sync?: boolean
    allowCreate?: boolean
    mode?: 'legacy' | 'v2'
    centroCustoDatabaseId?: string
    tipoDespesaDatabaseId?: string
    /** Sincroniza só uma linha V2 (ex.: após solis_abrir_linha_orcamento_solicitada) */
    chaveUnicaV2?: string
    /** Quando sync=true, processa só um lote para evitar WORKER_LIMIT na Edge Function */
    batchOffset?: number
    batchSize?: number
  }) => {
    return callEdgeFunction('sync-orcamento-pipefy', {
      method: 'POST',
      body: data,
    })
  },
  getOrcamento: async (data: any) => {
    return callEdgeFunction('get-orcamento-pipefy', {
      method: 'POST',
      body: data,
    })
  },
  testQuery: async (data: any) => {
    return callEdgeFunction('test-query-pipefy', {
      method: 'POST',
      body: data,
    })
  },
  createDatabase: async (data: {
    name: string
    description?: string
    organizationId?: string
  }) => {
    return callEdgeFunction('create-database-pipefy', {
      method: 'POST',
      body: data,
    })
  },
  listDatabases: async (data?: {
    organizationId?: string
  }) => {
    // Usar a mesma função mas com um parâmetro especial no body
    const result = await callEdgeFunction('create-database-pipefy', {
      method: 'POST',
      body: { ...data, _action: 'list' },
    })
    return result
  },
  createField: async (data: {
    tableId: string
    type: string
    label: string
    required?: boolean
    description?: string
    connectedRepoId?: string
  }) => {
    return callEdgeFunction('create-database-pipefy', {
      method: 'POST',
      body: { ...data, _action: 'createField' },
    })
  },
  listFields: async (data: {
    tableId: string
  }) => {
    return callEdgeFunction('create-database-pipefy', {
      method: 'POST',
      body: { ...data, _action: 'listFields' },
    })
  },
  listTableRecords: async (data: { tableId: string }) => {
    return callEdgeFunction('create-database-pipefy', {
      method: 'POST',
      body: { ...data, _action: 'listRecords' },
    })
  },
  syncSubtipos: async (data?: {
    centroCustoId?: string
  }) => {
    return callEdgeFunction('sync-subtipos-pipefy', {
      method: 'POST',
      body: data || {},
    })
  },
  syncTipos: async (data: {
    tiposDatabaseId: string
    ano: number
    allowCreate?: boolean
    allowDeactivate?: boolean
    sourceMode?: 'orcamento' | 'realizado_sem_orcamento'
  }) => {
    return callEdgeFunction('sync-tipos-pipefy', {
      method: 'POST',
      body: data,
    })
  },
  syncN3Global: async (data: {
    n3CatalogTableId: string
    tiposTableId: string
    ano: number
    allowCreate?: boolean
    allowDeactivate?: boolean
    sourceMode?: 'orcamento' | 'realizado_sem_orcamento'
  }) => {
    return callEdgeFunction('sync-n3-global-pipefy', {
      method: 'POST',
      body: data,
    })
  },
  verifyN3Global: async (data: {
    n3CatalogTableId: string
    tiposTableId: string
    ano: number
    sourceMode?: 'orcamento' | 'realizado_sem_orcamento'
  }) => {
    return callEdgeFunction('sync-n3-global-pipefy', {
      method: 'POST',
      body: { ...data, _action: 'verify' },
    })
  },
  verifySubtipos: async (data?: {
    centroCustoId?: string
  }) => {
    return callEdgeFunction('sync-subtipos-pipefy', {
      method: 'POST',
      body: { ...(data || {}), _action: 'verify' },
    })
  },
  syncAreaEscopoPreview: async (body?: { rootCentroId?: string }) => {
    return callEdgeFunction('sync-area-escopo-pipefy', {
      method: 'POST',
      body: { ...(body || {}), _action: 'preview' },
    })
  },
  syncAreaEscopo: async (body: { rootCentroId: string; pipefyTableId?: string }) => {
    return callEdgeFunction('sync-area-escopo-pipefy', {
      method: 'POST',
      body: { ...body, _action: 'sync' },
    })
  },
  listPipes: async (data?: {
    organizationId?: string
  }) => {
    return callEdgeFunction('list-webhooks-pipefy', {
      method: 'POST',
      body: data || {},
    })
  },
  getPipeWebhooks: async (data: {
    pipeId: string
  }) => {
    return callEdgeFunction('list-webhooks-pipefy', {
      method: 'POST',
      body: data,
    })
  },
}

// Pipeline
export const pipelineApi = {
  getActivityLog: async (params: any) => {
    return callEdgeFunction('pipeline-activity-log', {
      method: 'GET',
    })
  },
  getCardComments: async (cardId: string) => {
    return callEdgeFunction('pipeline-card-comments', {
      method: 'GET',
    })
  },
  createCardComment: async (data: any) => {
    return callEdgeFunction('pipeline-card-comments', {
      method: 'POST',
      body: data,
    })
  },
}

/**
 * Helper para migrar chamadas antigas de API routes
 * Converte /api/pipefy/sync-databases para sync-databases-pipefy
 */
export async function fetchApi(endpoint: string, options: RequestInit = {}) {
  // Mapear endpoint antigo para nome da Edge Function
  const endpointMap: Record<string, string> = {
    '/api/notifications': 'notifications',
    '/api/admin-invite-user': 'admin-invite-user',
    '/api/pipefy/sync-databases': 'sync-databases-pipefy',
    '/api/pipefy/sync-orcamento': 'sync-orcamento-pipefy',
    '/api/pipefy/get-orcamento': 'get-orcamento-pipefy',
    '/api/pipefy/test-query': 'test-query-pipefy',
    '/api/pipeline-activity-log': 'pipeline-activity-log',
    '/api/pipeline-card-comments': 'pipeline-card-comments',
  }

  const functionName = endpointMap[endpoint] || endpoint.replace('/api/', '').replace(/\//g, '-')
  
  return callEdgeFunction(functionName, {
    method: (options.method as any) || 'POST',
    body: options.body ? JSON.parse(options.body as string) : undefined,
    headers: options.headers as Record<string, string>,
  })
}

